import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatBRL, fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { processAndUploadImage } from "../lib/imageUpload";
import {
  PuzzlePiece, Camera, X, Package, PencilSimple, Stack,
  Gift, ShieldCheck, MagnifyingGlass, ArrowRight, Copy, Calendar, Users, ArrowLeft,
} from "@phosphor-icons/react";

const KIND = {
  variant: { label: "Variante", color: "#7C3AED", bg: "bg-[#7C3AED]/10", text: "text-[#7C3AED]" },
  addon: { label: "Opcional", color: "#004E98", bg: "bg-[#004E98]/10", text: "text-[#004E98]" },
  consumable: { label: "Consumível", color: "#FF6B35", bg: "bg-[#FF6B35]/10", text: "text-[#FF6B35]" },
};

/** Miniatura editável (upload de foto direto na tela). */
function InlineThumbPicker({ value, onChange, size = 64 }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const { path } = await processAndUploadImage(f);
      onChange(path);
    } catch (err) {
      toast.error(err?.message || "Falha ao enviar a foto");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={pick} />
      {value ? (
        <>
          <button type="button" onClick={() => ref.current?.click()}
            className="block w-full h-full rounded-xl overflow-hidden border-2 border-slate-200 hover:border-[#004E98] transition"
            data-testid="mc-thumb">
            <img src={fileUrl(value)} alt="" className="w-full h-full object-cover" />
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); onChange(""); }}
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center"
            data-testid="mc-thumb-clear">
            <X size={11} weight="bold" />
          </button>
        </>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
          className="w-full h-full rounded-xl border-2 border-dashed border-slate-300 hover:border-[#004E98] flex flex-col items-center justify-center text-slate-400"
          data-testid="mc-thumb">
          {busy ? <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" /> : <Camera size={size >= 56 ? 22 : 16} weight="bold" />}
        </button>
      )}
    </div>
  );
}

/** Modal de edição rápida — atualiza um único complemento (nome/preço/foto/qtd máx).
 *  Para variantes, o nome é comum e cada opção tem label/price_delta/foto editáveis. */
function EditComplementModal({ item, complementIndex, onClose, onSaved }) {
  const original = (item?.complements || [])[complementIndex] || null;
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(original || {})));
  const [saving, setSaving] = useState(false);
  if (!original) return null;
  const kind = (draft.kind || "").toLowerCase();

  const save = async () => {
    setSaving(true);
    try {
      const newComps = (item.complements || []).map((c, i) => i === complementIndex ? {
        kind: draft.kind,
        name: (draft.name || "").trim(),
        required: !!draft.required,
        charge: c.charge || "per_rental",
        price: parseFloat(draft.price || 0) || 0,
        max_qty: Math.max(1, parseInt(draft.max_qty || 1, 10) || 1),
        image: kind === "variant" ? null : ((draft.image || "").trim() || null),
        // V17.5: estoque próprio + pausa
        stock_qty: kind === "variant" ? null : (
          draft.stock_qty === "" || draft.stock_qty == null ? null : Math.max(0, parseInt(draft.stock_qty, 10) || 0)
        ),
        paused: kind === "variant" ? false : !!draft.paused,
        require_active: kind === "addon" && (draft.require_mode ? draft.require_mode !== "optional" : !!draft.require_active),
        require_mode: kind === "addon" ? (draft.require_mode || (draft.require_active ? "proportional" : "optional")) : "optional",
        require_min_fixed: Math.max(1, parseInt(draft.require_min_fixed || 1, 10) || 1),
        require_per_source: draft.require_per_source === "measure" ? "measure" : "quantity",
        require_per_base_qty: Math.max(1, parseInt(draft.require_per_base_qty || 1, 10) || 1),
        require_ratio: Math.max(1, parseInt(draft.require_ratio || 1, 10) || 1),
        options: kind === "variant"
          ? (() => {
              const opts = (draft.options || []).filter((o) => (o.label || "").trim());
              if (!opts.length) return null;
              const hasDefault = opts.some((o) => !!o.is_default);
              return opts.map((o, oi) => ({
                label: (o.label || "").trim(),
                price_delta: (o.is_default || (!hasDefault && oi === 0)) ? 0 : Math.max(0, parseFloat(o.price_delta || 0) || 0),
                image: (o.image || "").trim() || null,
                is_default: hasDefault ? !!o.is_default : oi === 0,
              }));
            })()
          : null,
      } : c);
      await api.patch(`/listings/${item.id}`, { complements: newComps });
      toast.success("Atualizado");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="mc-edit-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <PuzzlePiece size={20} weight="bold" className="text-[#004E98]" />
            <div>
              <div className="text-sm font-black text-slate-800">Editar {(KIND[kind] || {}).label || "complemento"}</div>
              <div className="text-[10px] text-slate-500">{item.title}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="mc-edit-close">
            <X size={22} weight="bold" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {/* Cabeçalho — nome + foto (para addon/consumable) */}
          {kind === "variant" ? (
            <label className="block">
              <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Nome da variante</span>
              <input value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                className="aj-input mt-1 font-bold" data-testid="mc-edit-name" />
            </label>
          ) : (
            <div className="flex gap-3 items-start">
              <InlineThumbPicker value={draft.image || ""} onChange={(v) => setDraft({ ...draft, image: v })} size={72} />
              <label className="block flex-1">
                <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Nome</span>
                <input value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  className="aj-input mt-1 font-bold" data-testid="mc-edit-name" />
              </label>
            </div>
          )}

          {kind === "variant" ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Opções</span>
                <label className="flex items-center gap-1 text-[11px] font-bold text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={!!draft.required}
                    onChange={(e) => setDraft({ ...draft, required: e.target.checked })}
                    className="w-3.5 h-3.5 accent-[#7C3AED]" data-testid="mc-edit-required" />
                  Obrigatória
                </label>
              </div>
              {(draft.options || []).map((o, oi) => (
                <div key={oi} className={`rounded-lg border-2 ${o.is_default ? "border-[#7C3AED] bg-[#7C3AED]/[0.04]" : "border-slate-200"} p-2 flex items-center gap-2`} data-testid={`mc-edit-opt-${oi}`}>
                  <InlineThumbPicker value={o.image || ""}
                    onChange={(v) => setDraft({ ...draft, options: draft.options.map((x, xi) => xi === oi ? { ...x, image: v } : x) })}
                    size={48} />
                  <div className="flex-1 min-w-0 space-y-1">
                    <input value={o.label || ""} placeholder={`Opção ${oi + 1}`}
                      onChange={(e) => setDraft({ ...draft, options: draft.options.map((x, xi) => xi === oi ? { ...x, label: e.target.value } : x) })}
                      className="aj-input py-2 text-sm" data-testid={`mc-edit-opt-${oi}-label`} />
                    <div className="flex items-center gap-2">
                      {o.is_default ? (
                        <div className="flex-1 rounded-lg border-2 border-dashed border-[#7C3AED]/40 bg-[#7C3AED]/5 px-2.5 py-2 flex items-center gap-1.5" data-testid={`mc-edit-opt-${oi}-base`} title="Opção padrão — usa o valor do aluguel">
                          <span className="text-[9px] font-black text-[#7C3AED] uppercase tracking-wider">Preço base</span>
                          <span className="text-sm font-black text-slate-800">{formatBRL(parseFloat(item.price_per_day || 0) || 0)}</span>
                        </div>
                      ) : (
                        <div className="relative flex-1">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] font-black text-emerald-600 pointer-events-none">+R$</span>
                          <input type="number" step="0.01" min="0" value={o.price_delta}
                            onChange={(e) => setDraft({ ...draft, options: draft.options.map((x, xi) => xi === oi ? { ...x, price_delta: e.target.value } : x) })}
                            className="aj-input py-2 text-sm pl-10" data-testid={`mc-edit-opt-${oi}-delta`} placeholder="0,00" />
                        </div>
                      )}
                      <label className="flex items-center gap-1 text-[10px] font-bold text-slate-600 cursor-pointer shrink-0">
                        <input type="radio" name="mc-default"
                          checked={!!o.is_default}
                          onChange={() => setDraft({ ...draft, options: draft.options.map((x, xi) => xi === oi ? { ...x, is_default: true, price_delta: 0 } : { ...x, is_default: false }) })}
                          className="w-3.5 h-3.5 accent-[#7C3AED]" data-testid={`mc-edit-opt-${oi}-default`} />
                        padrão
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Preço (R$)</span>
                  <div className="relative mt-1">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] font-black text-slate-400 pointer-events-none">R$</span>
                    <input type="number" step="0.01" min="0" placeholder="10,00" value={draft.price}
                      onChange={(e) => setDraft({ ...draft, price: e.target.value })}
                      className="aj-input pl-8" data-testid="mc-edit-price" />
                  </div>
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Qtd. máxima</span>
                  <input type="number" step="1" min="1" value={draft.max_qty}
                    onChange={(e) => setDraft({ ...draft, max_qty: e.target.value })}
                    className="aj-input mt-1" data-testid="mc-edit-maxqty" />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2 border-t border-slate-100 pt-2">
                <label className="block">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">
                    {kind === "consumable" ? "Estoque disponível" : "Unidades disponíveis"}
                  </span>
                  <input type="number" step="1" min="0" placeholder="Vazio p/ ilimitado" value={draft.stock_qty ?? ""}
                    onChange={(e) => setDraft({ ...draft, stock_qty: e.target.value })}
                    className="aj-input mt-1" data-testid="mc-edit-stock-qty" />
                </label>
                <label className="flex items-end gap-2 pb-2 cursor-pointer">
                  <input type="checkbox" checked={!!draft.paused}
                    onChange={(e) => setDraft({ ...draft, paused: e.target.checked })}
                    className="w-4 h-4 accent-amber-500 mb-1" data-testid="mc-edit-paused" />
                  <span className="text-[11px] font-bold text-slate-700 mb-1">
                    Pausar temporariamente
                    <span className="block text-[10px] font-normal text-slate-500">Some do checkout do cliente sem excluir.</span>
                  </span>
                </label>
              </div>
            </>
          )}
          <Link to={`/editar-anuncio/${item.id}`}
            className="text-[11px] font-bold text-[#004E98] inline-flex items-center gap-1"
            data-testid="mc-edit-open-full">
            <PencilSimple size={12} weight="bold" /> Editar tudo no anúncio
          </Link>
        </div>
        <div className="sticky bottom-0 bg-white p-4 border-t border-slate-200 flex gap-2">
          <button type="button" onClick={onClose}
            className="flex-1 rounded-xl border-2 border-slate-200 py-2.5 text-sm font-bold" data-testid="mc-edit-cancel">
            Cancelar
          </button>
          <button type="button" onClick={save} disabled={saving}
            className="flex-1 rounded-xl bg-[#004E98] text-white py-2.5 text-sm font-bold disabled:opacity-50" data-testid="mc-edit-save">
            {saving ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** V17.5+: Modal de reservas ativas de um complemento (visão do dono). */
function ReservationsModal({ payload, onClose }) {
  const { listing, comp, comp_index } = payload;
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const r = await api.get(`/listings/${listing.id}/complements/${comp_index}/reservations`);
        if (!ignore) setRows(r.data?.reservations || []);
      } catch (e) {
        if (!ignore) setError(formatApiError(e.response?.data?.detail) || "Falha ao carregar reservas");
      }
    })();
    return () => { ignore = true; };
  }, [listing.id, comp_index]);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="mc-reservations-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Users size={20} weight="bold" className="text-[#004E98]" />
            <div>
              <div className="text-sm font-black text-slate-800">Reservas ativas — {comp.name}</div>
              <div className="text-[10px] text-slate-500">{listing.title}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="mc-reservations-close">
            <X size={22} weight="bold" />
          </button>
        </div>
        <div className="p-4 space-y-2">
          {rows == null && !error && <div className="text-center py-8 text-slate-500 text-sm">Carregando…</div>}
          {error && <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 p-2 text-sm">{error}</div>}
          {rows && rows.length === 0 && <div className="text-center py-6 text-slate-500 text-sm" data-testid="mc-reservations-empty">Nenhuma reserva ativa usando este complemento.</div>}
          {rows && rows.map((r) => (
            <div key={r.booking_id} className="rounded-xl border border-slate-200 p-2.5 flex items-center gap-2" data-testid={`mc-reservation-${r.booking_id}`}>
              <Calendar size={16} weight="bold" className="text-slate-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-black text-slate-800 truncate">{r.renter_name}</div>
                <div className="text-[11px] text-slate-500">
                  {r.start_date} → {r.end_date} · <span className="uppercase text-[9px] font-bold">{r.status}</span>
                </div>
              </div>
              <span className="text-[11px] font-black bg-slate-100 rounded-full px-2 py-0.5">{r.qty}×</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** V17.5+: Duplica um complemento inteiro (nome, preço, foto, regras, estoque) em outro anúncio do dono. */
function DuplicateComplementModal({ source, comp, listings, onClose, onDone }) {
  const [targetId, setTargetId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const targets = (listings || []).filter((l) => l.id !== source.id);

  const submit = async () => {
    if (!targetId) { setError("Escolha um anúncio de destino."); return; }
    setSaving(true); setError("");
    try {
      const target = listings.find((l) => l.id === targetId);
      if (!target) throw new Error("Anúncio de destino não encontrado");
      const newComps = [...(target.complements || []), {
        kind: comp.kind,
        name: comp.name,
        required: !!comp.required,
        charge: comp.charge || "per_rental",
        price: parseFloat(comp.price || 0) || 0,
        max_qty: Math.max(1, parseInt(comp.max_qty || 1, 10) || 1),
        image: comp.kind === "variant" ? null : (comp.image || null),
        stock_qty: comp.kind === "variant" ? null : (comp.stock_qty ?? null),
        paused: !!comp.paused,
        require_active: comp.kind === "addon" ? !!comp.require_active : false,
        require_mode: comp.require_mode || "optional",
        require_min_fixed: comp.require_min_fixed || 1,
        require_per_source: comp.require_per_source || "quantity",
        require_per_base_qty: comp.require_per_base_qty || 1,
        require_ratio: comp.require_ratio || 1,
        options: comp.kind === "variant" ? (comp.options || []).map((o) => ({
          label: o.label, price_delta: parseFloat(o.price_delta || 0) || 0,
          image: o.image || null, is_default: !!o.is_default,
        })) : null,
      }];
      await api.patch(`/listings/${targetId}`, { complements: newComps });
      toast.success(`Complemento adicionado em "${target.title}".`);
      onDone();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message || "Falha ao duplicar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="mc-duplicate-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Copy size={20} weight="bold" className="text-emerald-600" />
            <div>
              <div className="text-sm font-black text-slate-800">Duplicar em outro anúncio</div>
              <div className="text-[10px] text-slate-500 truncate">{comp.name} · de {source.title}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400" data-testid="mc-duplicate-close">
            <X size={22} weight="bold" />
          </button>
        </div>
        <div className="p-4 space-y-2">
          <label className="block">
            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Anúncio de destino</span>
            <select value={targetId} onChange={(e) => setTargetId(e.target.value)}
              className="aj-input mt-1" data-testid="mc-duplicate-target">
              <option value="">Escolha um anúncio…</option>
              {targets.map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
            </select>
          </label>
          <div className="text-[11px] text-slate-500 bg-slate-50 rounded p-2 leading-relaxed">
            O complemento será adicionado ao anúncio escolhido com nome, foto, preço, regras e estoque idênticos. Você pode editar depois.
          </div>
          {error && <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 p-2 text-xs">{error}</div>}
        </div>
        <div className="p-4 border-t border-slate-200 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 rounded-xl border-2 border-slate-200 py-2.5 text-sm font-bold" data-testid="mc-duplicate-cancel">Cancelar</button>
          <button type="button" onClick={submit} disabled={saving || !targetId}
            className="flex-1 rounded-xl bg-emerald-600 text-white py-2.5 text-sm font-bold disabled:opacity-50" data-testid="mc-duplicate-submit">
            {saving ? "Duplicando…" : "Duplicar aqui"}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function MyComplements() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState("all");
  const [listingFilter, setListingFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [edit, setEdit] = useState(null); // { item, complementIndex }
  const [duplicate, setDuplicate] = useState(null); // { source_listing, comp }
  const [reservations, setReservations] = useState(null); // { listing, comp, rows }
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/my/listings");
      setListings(r.data || []);
    } catch (e) {
      console.error("[MyComplements] load failed", e?.message);
      toast.error("Falha ao carregar seus anúncios.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Aplanar: [{listing, comp, compIndex}]
  const flat = useMemo(() => {
    const rows = [];
    for (const l of listings) {
      for (let i = 0; i < (l.complements || []).length; i++) {
        rows.push({ listing: l, comp: l.complements[i], compIndex: i });
      }
    }
    return rows;
  }, [listings]);

  const counts = useMemo(() => {
    const c = { all: flat.length, variant: 0, addon: 0, consumable: 0 };
    for (const r of flat) c[(r.comp.kind || "").toLowerCase()] = (c[r.comp.kind] || 0) + 1;
    return c;
  }, [flat]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return flat.filter((r) => {
      if (kindFilter !== "all" && r.comp.kind !== kindFilter) return false;
      if (listingFilter !== "all" && r.listing.id !== listingFilter) return false;
      if (!q) return true;
      const inName = (r.comp.name || "").toLowerCase().includes(q);
      const inListing = (r.listing.title || "").toLowerCase().includes(q);
      const inOptions = (r.comp.options || []).some((o) => (o.label || "").toLowerCase().includes(q));
      return inName || inListing || inOptions;
    });
  }, [flat, kindFilter, listingFilter, search]);

  // Agrupa por listing.id preservando ordem
  const groups = useMemo(() => {
    const byId = new Map();
    for (const r of filtered) {
      if (!byId.has(r.listing.id)) byId.set(r.listing.id, { listing: r.listing, rows: [] });
      byId.get(r.listing.id).rows.push(r);
    }
    return [...byId.values()];
  }, [filtered]);

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="max-w-4xl mx-auto p-4 space-y-4" data-testid="mc-page">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => nav("/meus-anuncios")}
            className="w-9 h-9 rounded-full border-2 border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-100 shrink-0"
            data-testid="mc-back-btn"
            aria-label="Voltar para meus anúncios">
            <ArrowLeft size={18} weight="bold" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-2xl sm:text-3xl font-black text-slate-900 flex items-center gap-2">
              <PuzzlePiece size={26} weight="fill" className="text-[#004E98]" />
              Meus adicionais
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Todos os complementos (variantes, opcionais e consumíveis) dos seus anúncios em um só lugar. Edite rapidinho sem abrir o anúncio inteiro.
            </p>
          </div>
        </div>

        {/* Filtros */}
        <div className="space-y-2">
          <div className="relative">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, opção ou anúncio..."
              className="aj-input pl-9" data-testid="mc-search" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[
              { key: "all", label: "Todos", icon: Stack },
              { key: "variant", label: "Variantes", icon: PuzzlePiece },
              { key: "addon", label: "Opcionais", icon: ShieldCheck },
              { key: "consumable", label: "Consumíveis", icon: Gift },
            ].map(({ key, label, icon: Icon }) => (
              <button key={key} type="button" onClick={() => setKindFilter(key)}
                className={`text-[11px] font-black rounded-full border-2 px-3 py-1.5 inline-flex items-center gap-1.5 transition ${kindFilter === key ? "border-[#004E98] bg-[#004E98] text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}
                data-testid={`mc-filter-${key}`}>
                <Icon size={12} weight="bold" /> {label}
                <span className={`text-[9px] rounded-full px-1.5 ${kindFilter === key ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>
                  {counts[key] || 0}
                </span>
              </button>
            ))}
          </div>
          {listings.length > 1 && (
            <select value={listingFilter} onChange={(e) => setListingFilter(e.target.value)}
              className="aj-input" data-testid="mc-filter-listing">
              <option value="all">Todos os anúncios</option>
              {listings.map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
            </select>
          )}
        </div>

        {/* Estados */}
        {loading && (
          <div className="text-center py-12 text-slate-500 text-sm" data-testid="mc-loading">Carregando…</div>
        )}
        {!loading && flat.length === 0 && (
          <div className="text-center py-12 rounded-2xl bg-white border-2 border-dashed border-slate-200" data-testid="mc-empty">
            <PuzzlePiece size={40} weight="duotone" className="mx-auto text-slate-300 mb-2" />
            <div className="text-sm font-bold text-slate-700">Você ainda não cadastrou nenhum complemento.</div>
            <div className="text-[11px] text-slate-500 mt-1">Abra um anúncio &gt; Configurações avançadas para adicionar variantes, opcionais e consumíveis.</div>
            <Link to="/meus-anuncios" className="inline-flex items-center gap-1 text-xs font-bold text-[#004E98] mt-3">
              Ir para meus anúncios <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        )}
        {!loading && flat.length > 0 && filtered.length === 0 && (
          <div className="text-center py-8 rounded-2xl bg-white border border-slate-200 text-sm text-slate-500" data-testid="mc-no-results">
            Nenhum resultado para o filtro atual.
          </div>
        )}

        {/* Grupos por item mãe */}
        {groups.map(({ listing, rows }) => (
          <div key={listing.id} className="rounded-2xl bg-white border-2 border-slate-200 overflow-hidden" data-testid={`mc-group-${listing.id}`}>
            <div className="flex items-center gap-3 p-3 bg-slate-50 border-b border-slate-200">
              {listing.images?.[0] && String(listing.images[0]).trim() ? (
                <img src={fileUrl(listing.images[0])} alt="" className="w-12 h-12 rounded-lg object-cover border border-slate-200" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-300"><Package size={20} weight="bold" /></div>
              )}
              <div className="flex-1 min-w-0">
                <Link to={`/itens/${listing.id}`} className="text-sm font-black text-slate-800 truncate block hover:text-[#004E98]" data-testid={`mc-listing-link-${listing.id}`}>
                  {listing.title}
                  <span className="text-[10px] font-normal text-slate-400 ml-1">#{String(listing.id).slice(-4)}</span>
                </Link>
                <div className="text-[10px] text-slate-500">{rows.length} complemento(s) · {formatBRL(listing.price_per_day)} base</div>
              </div>
              <Link to={`/editar-anuncio/${listing.id}`}
                className="text-[11px] font-bold text-[#004E98] inline-flex items-center gap-1 shrink-0"
                data-testid={`mc-edit-listing-${listing.id}`}>
                <PencilSimple size={11} weight="bold" /> Anúncio
              </Link>
            </div>
            <div className="p-3 space-y-2">
              {rows.map(({ comp, compIndex }) => {
                const meta = KIND[comp.kind] || KIND.addon;
                const optsSummary = (comp.options || []).slice(0, 3).map((o) => o.label).filter(Boolean).join(" · ");
                return (
                  <button key={compIndex} type="button" onClick={() => setEdit({ item: listing, complementIndex: compIndex })}
                    className="w-full text-left rounded-xl border border-slate-200 hover:border-[#004E98] hover:bg-slate-50 p-2.5 flex items-center gap-3 transition"
                    data-testid={`mc-row-${listing.id}-${compIndex}`}>
                    {/* Thumb: variante mostra primeira opção com foto, addon/consumable usa comp.image */}
                    {(() => {
                      const img = comp.kind === "variant"
                        ? (comp.options || []).map((o) => o.image).find(Boolean)
                        : comp.image;
                      if (img) return <img src={fileUrl(img)} alt="" className="w-14 h-14 rounded-lg object-cover border border-slate-200 shrink-0" />;
                      return (
                        <div className="w-14 h-14 rounded-lg bg-slate-100 border border-slate-200 shrink-0 flex items-center justify-center text-slate-300">
                          <Package size={20} weight="bold" />
                        </div>
                      );
                    })()}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-[9px] uppercase font-black tracking-wider px-2 py-0.5 rounded-full text-white`} style={{ background: meta.color }}>
                          {meta.label}
                        </span>
                        <span className="text-sm font-black text-slate-800 truncate">{comp.name}</span>
                      </div>
                      {comp.kind === "variant" && optsSummary && (
                        <div className="text-[11px] text-slate-500 truncate mt-0.5">{optsSummary}{(comp.options || []).length > 3 ? "…" : ""}</div>
                      )}
                      {comp.kind !== "variant" && (
                        <div className="text-[11px] text-slate-500 flex items-center gap-1 flex-wrap">
                          <span>{formatBRL(parseFloat(comp.price || 0) || 0)} · máx {comp.max_qty || 1}</span>
                          {comp.stock_qty != null && parseInt(comp.stock_qty, 10) > 0 && (
                            <span className="text-[10px] font-bold text-slate-700 bg-slate-100 border border-slate-200 rounded px-1.5">
                              estoque: {comp.stock_qty}
                            </span>
                          )}
                          {!!comp.paused && (
                            <span className="text-[9px] font-black uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-1.5 py-0.5" data-testid={`mc-row-paused-${listing.id}-${compIndex}`}>
                              Pausado
                            </span>
                          )}
                          {comp.kind === "addon" && comp.require_active && (
                            <span className="text-[10px] font-bold text-amber-700">
                              {comp.require_mode === "fixed"
                                ? `· obrig. mín ${comp.require_min_fixed || 1}`
                                : `· obrig. ${comp.require_ratio}/${comp.require_per_base_qty} ${comp.require_per_source === "measure" ? "med." : "un."}`}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {comp.kind !== "variant" && (
                        <button type="button"
                          onClick={(e) => { e.stopPropagation(); setReservations({ listing, comp, comp_index: compIndex }); }}
                          className="p-1 text-slate-400 hover:text-[#004E98] transition"
                          title="Ver quem está usando"
                          data-testid={`mc-row-reservations-${listing.id}-${compIndex}`}>
                          <Users size={16} weight="bold" />
                        </button>
                      )}
                      {listings.length > 1 && (
                        <button type="button"
                          onClick={(e) => { e.stopPropagation(); setDuplicate({ source_listing: listing, comp }); }}
                          className="p-1 text-slate-400 hover:text-emerald-600 transition"
                          title="Duplicar em outro anúncio"
                          data-testid={`mc-row-duplicate-${listing.id}-${compIndex}`}>
                          <Copy size={16} weight="bold" />
                        </button>
                      )}
                      <PencilSimple size={16} weight="bold" className="text-slate-400" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {edit && <EditComplementModal item={edit.item} complementIndex={edit.complementIndex}
        onClose={() => setEdit(null)}
        onSaved={() => { setEdit(null); load(); }} />}
      {duplicate && <DuplicateComplementModal source={duplicate.source_listing} comp={duplicate.comp} listings={listings}
        onClose={() => setDuplicate(null)}
        onDone={() => { setDuplicate(null); load(); }} />}
      {reservations && <ReservationsModal payload={reservations} onClose={() => setReservations(null)} />}
    </div>
  );
}
